import type { OsSignals, SensorSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt, attemptAsync, delay, withTimeout } from '../util/safe.js';
import { hashValue } from '../util/hash.js';

/**
 * Маркеры операционной системы, не зависящие от UA.
 *
 * Голоса синтеза речи и матрица поддержки кодеков приходят из системы, а не из
 * браузера. Подделать их согласованно почти никто не пытается: спуферы правят
 * `navigator.platform` и UA, а список голосов остаётся родным. Поэтому набор
 * «Microsoft David» под видом macOS — практически готовый вердикт.
 */

const CODEC_PROBES: [string, string][] = [
  ['h264', 'video/mp4; codecs="avc1.42E01E"'],
  ['h265', 'video/mp4; codecs="hvc1.1.6.L93.B0"'],
  ['vp8', 'video/webm; codecs="vp8"'],
  ['vp9', 'video/webm; codecs="vp9"'],
  ['av1', 'video/mp4; codecs="av01.0.05M.08"'],
  ['theora', 'video/ogg; codecs="theora"'],
  ['aac', 'audio/mp4; codecs="mp4a.40.2"'],
  ['mp3', 'audio/mpeg'],
  ['opus', 'audio/webm; codecs="opus"'],
  ['vorbis', 'audio/ogg; codecs="vorbis"'],
  ['flac', 'audio/flac'],
  ['wav', 'audio/wav; codecs="1"'],
  ['aiff', 'audio/aiff'],
  ['m4a', 'audio/x-m4a'],
  ['caf', 'audio/x-caf'],
  ['wmv', 'video/x-ms-wmv'],
];

/** Характерные префиксы имён голосов для каждой платформы. */
const VOICE_MARKERS: [RegExp, string][] = [
  [/^Microsoft /, 'windows'],
  [/^(Alex|Samantha|Daniel|Karen|Moira|Fiona|Tessa|Victoria|Fred|Albert)$/, 'macos'],
  [/^(Alice|Alva|Amelie|Anna|Carmit|Damayanti|Ellen|Ioana|Joana|Kanya|Kyoko|Lekha|Luciana)$/, 'macos'],
  [/^Google /, 'android-or-chromeos'],
  [/^(espeak|eSpeak|Festival|pico)/, 'linux'],
];

async function collectVoices(): Promise<SpeechSynthesisVoice[] | null> {
  return attemptAsync(async () => {
    const synth = globalThis.speechSynthesis;
    if (!synth?.getVoices) return null;

    const immediate = synth.getVoices();
    if (immediate.length > 0) return immediate;

    // В Chrome список приходит асинхронно, событием voiceschanged.
    // Пустой список после ожидания — тоже результат: в headless голосов нет.
    return withTimeout(
      new Promise<SpeechSynthesisVoice[]>((resolve) => {
        const handler = () => resolve(synth.getVoices());
        synth.addEventListener('voiceschanged', handler, { once: true });
      }),
      1500,
      'voices',
    ).catch(() => synth.getVoices());
  });
}

function guessOsFromVoices(voices: { name: string; localService: boolean }[]): string | null {
  const local = voices.filter((v) => v.localService);
  const pool = local.length > 0 ? local : voices;
  if (pool.length === 0) return null;

  const counts: Record<string, number> = {};
  for (const voice of pool) {
    for (const [pattern, os] of VOICE_MARKERS) {
      if (pattern.test(voice.name)) {
        counts[os] = (counts[os] ?? 0) + 1;
        break;
      }
    }
  }

  let best: string | null = null;
  let bestCount = 0;
  for (const [os, count] of Object.entries(counts)) {
    if (count > bestCount) {
      bestCount = count;
      best = os;
    }
  }
  return best;
}

function collectCodecs(): Record<string, string> | null {
  return attempt(() => {
    const video = document.createElement('video');
    const audio = document.createElement('audio');
    const mediaSource = (globalThis as { MediaSource?: { isTypeSupported?: (t: string) => boolean } })
      .MediaSource;

    const out: Record<string, string> = {};
    for (const [name, type] of CODEC_PROBES) {
      const element = type.startsWith('audio') ? audio : video;
      const canPlay = attempt(() => element.canPlayType(type), '') ?? '';
      const mse = mediaSource?.isTypeSupported
        ? (attempt(() => mediaSource.isTypeSupported!(type), false) ?? false)
        : null;
      // Два независимых источника: canPlayType отвечает за декодер,
      // MediaSource — за поддержку в потоковом воспроизведении.
      out[name] = `${canPlay || 'no'}/${mse === null ? 'na' : mse ? 'mse' : 'no-mse'}`;
    }
    return out;
  });
}

function guessOsFromCodecs(codecs: Record<string, string> | null): string | null {
  if (!codecs) return null;
  const has = (name: string): boolean => !!codecs[name] && !codecs[name].startsWith('no/');

  // Аппаратный H.265 в браузере есть на Apple и на современных Windows,
  // а вот CAF и AIFF — специфика Apple.
  if (has('caf') || has('aiff')) return 'macos-or-ios';
  if (has('wmv')) return 'windows';
  if (has('h265') && !has('caf')) return 'windows';
  return null;
}

/**
 * Датчики движения.
 *
 * Различать наличие интерфейса и наличие самого датчика обязательно:
 * конструктор `DeviceMotionEvent` есть и в настольном Chrome, а события
 * оттуда не приходят никогда. Поэтому проверяется и то, и другое, но
 * выводы правила строит только на полном отсутствии интерфейса — короткого
 * ожидания события мало, чтобы утверждать, что датчика нет.
 */
async function collectSensors(): Promise<SensorSignals> {
  const hasDeviceMotion = 'DeviceMotionEvent' in globalThis;
  const hasDeviceOrientation = 'DeviceOrientationEvent' in globalThis;

  let motionFired = false;
  let orientationFired = false;

  await attemptAsync(async () => {
    const onMotion = () => {
      motionFired = true;
    };
    const onOrientation = () => {
      orientationFired = true;
    };
    addEventListener('devicemotion', onMotion, { once: true });
    addEventListener('deviceorientation', onOrientation, { once: true });

    // Полсекунды: телефон в руке присылает события несколько раз в секунду,
    // а ждать дольше означает задерживать весь отчёт.
    await delay(500);

    removeEventListener('devicemotion', onMotion);
    removeEventListener('deviceorientation', onOrientation);
  });

  return { hasDeviceMotion, hasDeviceOrientation, motionFired, orientationFired };
}

export const osModule = defineModule({
  id: 'os',
  phase: 1,
  timeoutMs: 5000,
  async run(): Promise<OsSignals> {
    const rawVoices = await collectVoices();
    const voices = rawVoices
      ? rawVoices.map((v) => ({
          name: v.name,
          lang: v.lang,
          localService: v.localService,
          default: v.default,
        }))
      : null;

    const codecs = collectCodecs();

    const mediaDevices = await attemptAsync(async () => {
      if (!navigator.mediaDevices?.enumerateDevices) return null;
      const devices = await navigator.mediaDevices.enumerateDevices();
      const counts: Record<string, number> = {};
      for (const device of devices) {
        counts[device.kind] = (counts[device.kind] ?? 0) + 1;
      }
      return Object.entries(counts).map(([kind, count]) => ({ kind, count }));
    });

    const mediaDevicesLabelled = await attemptAsync(async () => {
      if (!navigator.mediaDevices?.enumerateDevices) return null;
      const devices = await navigator.mediaDevices.enumerateDevices();
      // Непустые метки означают выданное разрешение на камеру/микрофон.
      return devices.some((d) => d.label !== '');
    });

    const layout = await attemptAsync(async () => {
      const keyboard = (navigator as Navigator & {
        keyboard?: { getLayoutMap?: () => Promise<Map<string, string>> };
      }).keyboard;
      if (!keyboard?.getLayoutMap) return null;
      const map = await keyboard.getLayoutMap();
      return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
    });

    return {
      sensors: await collectSensors(),
      voices,
      voicesHash: voices ? hashValue(voices.map((v) => `${v.name}|${v.lang}|${v.localService}`)) : null,
      voicesOsGuess: voices ? guessOsFromVoices(voices) : null,
      codecs,
      codecsHash: codecs ? hashValue(codecs) : null,
      codecsOsGuess: guessOsFromCodecs(codecs),
      mediaDevices,
      mediaDevicesLabelled,
      keyboardLayoutHash: layout ? hashValue(layout) : null,
      keyboardLayoutSize: layout ? layout.length : null,
      touchSupport: {
        maxTouchPoints: attempt(() => navigator.maxTouchPoints),
        touchEvent: attempt(() => {
          document.createEvent('TouchEvent');
          return true;
        }, false) ?? false,
        touchStart: 'ontouchstart' in globalThis,
      },
    };
  },
});
